<template>
  <v-container fluid class="b-container">
    <v-sheet class="pa-3" outlined style="overflow: auto">
      <v-toolbar flat height="90" class="mb-3">
        <div class="d-flex flex-column pt-6">
          <v-btn
            :disabled="!getPermission('loyalty.create')"
            color="secondary"
            class="text-capitalize px-5"
            depressed
            @click="openForm({}, 'create')"
            >{{ translations.createLoyaltyProgram[lang] }}</v-btn
          >
          <v-col> </v-col>
        </div>

        <v-spacer></v-spacer>

        <v-col cols="12" md="4" class="pa-0 mb-0 d-flex align-end" style="height: 100%">
          <b-search-field v-model="search"></b-search-field>
        </v-col>
      </v-toolbar>
      <v-data-table
        :headers="headers"
        :loading="loading"
        :items="loyaltyPrograms"
        :footer-props="{
          itemsPerPageOptions: [12],
          showCurrentPage: true,
          pageText: `${page * 12 - 11}-${
            page * 12 <= pagination.total ? page * 12 : pagination.total
          } ${translations.paginationText[lang]} ${pagination.total}`,
        }"
        :page.sync="page"
        :server-items-length="pagination.total"
        :options.sync="options"
        class="b-outlined"
      >
        <template v-slot:item.active="{ item }">
          <v-switch
            class="icon-switch"
            v-model="item.active"
            inset
            @change="switchActive(item)"
          ></v-switch>
        </template>
        <template v-slot:item.logo="{ item }">
          <v-img contain height="36" width="36" :src="item.logo"></v-img>
        </template>

        <template v-slot:item.actions="{ item }">
          <v-tooltip color="secondary" top>
            <template v-slot:activator="{ on }">
              <v-btn
                :disabled="!getPermission('loyalty.updates')"
                color="yellow darken-3"
                icon
                v-on="on"
                @click="openForm(item, 'update')"
              >
                <v-icon v-text="icons.mdiPencilOutline"></v-icon>
              </v-btn>
            </template>

            <span class="font-weight-bold">{{ translations.edit[lang] }}</span>
          </v-tooltip>
          <v-tooltip color="secondary" top>
            <template v-slot:activator="{ on }">
              <v-btn
                :disabled="!(async () => await getPermission('loyalty.delete'))"
                color="red"
                icon
                v-on="on"
                @click="
                  () => {
                    openDeleteDialog(item);
                  }
                "
              >
                <v-icon v-text="icons.mdiClose"></v-icon>
              </v-btn>
            </template>

            <span class="font-weight-bold" v-text="translations.delete[lang]"></span>
          </v-tooltip>
        </template>
      </v-data-table>
      <v-dialog v-model="dialog" :max-width="$vuetify.breakpoint.smAndDown ? '90%' : '40%'">
        <LoyaltyProgramForm @close="dialog = false" :mode="this.mode" />
      </v-dialog>

      <v-dialog v-model="deleteDialog" max-width="500">
        <b-card
          v-if="deletingLoyalty.loyalty_program_id"
          type="delete"
          :title="translations.deleteLoyaltyProgram[lang]"
          :loading="loading"
          :error-message="errorMessage"
          :submit-text="translations.delete"
          @cancel="deleteDialog = false"
          @submit="deleteLoyalty(deletingLoyalty.loyalty_program_id)"
        >
          <div class="subtitle-1 font-weight-medium pl-2">
            {{ translations.deleteModalText[lang] }}
            <b
              >{{ deletingLoyalty.name[lang] }} ( <i>{{ deletingLoyalty.points_label }}</i> )</b
            >
            <span class="font-weight-bold text--primary"></span>?
          </div>
        </b-card>
      </v-dialog>
    </v-sheet>
  </v-container>
</template>

<script>
import { mdiPencilOutline, mdiClose, mdiCheckCircleOutline, mdiMinusCircleOutline } from '@mdi/js';
import { mapMutations, mapState, mapActions } from 'vuex';
import LoyaltyProgramForm from '../../components/loyaltyPanel/loyaltyPrograms/LoyaltyProgramForm';
import translations from '@/utils/translations/loyaltyPanel/loyaltyPrograms';
import { debounce, cloneDeep } from 'lodash';

export default {
  name: 'LoyaltyPrograms',

  components: { LoyaltyProgramForm },

  mixins: [translations],

  data() {
    return {
      icons: {
        mdiPencilOutline,
        mdiClose,
        mdiCheckCircleOutline,
        mdiMinusCircleOutline,
      },
      page: +this.$route.query.page,
      deletingLoyalty: {},
      search: '',
      mode: '',
      options: {},
      deleteDialog: false,
      activeLoadingId: 0,
      dialog: false,
    };
  },

  computed: {
    ...mapState('loyaltyPanel/loyaltyPrograms', ['loyaltyPrograms', 'pagination']),
    ...mapState(['loading', 'errorMessage']),

    pagination() {
      return this.$store.state.loyaltyPanel.loyaltyPrograms.pagination || {};
    },

    lang() {
      return this.$route.params.lang;
    },
    storeMode() {
      return this.$store.state.store?.display_mode || 'points';
    },
    headers() {
      return [
        {
          text: this.translations.name[this.lang],
          value: `name[${this.lang}]`,
        },
        {
          text: this.translations.description[this.lang],
          value: `description[${this.lang}]`,
        },
        {
          text: this.translations[this.storeMode + 'Label'][this.lang],
          value: 'points_label',
        },
        {
          text: 'Logo',
          value: 'logo',
          sortable: false,
        },
        {
          text: this.translations.active[this.lang],
          value: 'active',
        },
        {
          text: this.translations.actions[this.lang],
          value: 'actions',
          sortable: false,
        },
      ];
    },

    query() {
      let query = '?';
      for (let key in this.$route.query) {
        query += `${key}=${this.$route.query[key]}&`;
      }
      return query.slice(0, query.length - 1);
    },
  },

  methods: {
    ...mapActions('loyaltyPanel/loyaltyPrograms', [
      'fetchLoyaltyPrograms',
      'deleteLoyaltyProgram',
      'toggleActive',
    ]),
    ...mapActions(['getPermission']),
    ...mapMutations('loyaltyPanel/loyaltyPrograms', ['setLoyaltyProgram']),
    ...mapMutations(['setNotification']),

    openForm(item, mode) {
      this.setLoyaltyProgram(cloneDeep(item));
      this.mode = mode;
      this.dialog = true;
    },

    closeForm(mode) {
      this.mode = mode;
      this.dialog = true;
    },

    openDeleteDialog(item) {
      this.deletingLoyalty = item;
      this.deleteDialog = true;
    },

    handleSearch() {
      this.page = 1;
      this.fetchLoyaltyPrograms(`?q=${this.search}`);
    },
    async switchActive(loyalty) {
      this.activeLoadingId = loyalty.loyalty_program_id;
      let res = await this.toggleActive({
        id: loyalty.loyalty_program_id,
        body: { active: loyalty.active },
      });
      if (!res || res.error) {
        loyalty.active = !loyalty.active;
        return this.setNotification({
          show: true,
          type: 'error',
          text: res.message,
        });
      }
      console.log(loyalty, 'switchActive');
      this.activeLoadingId = 0;
    },
    async deleteLoyalty() {
      await this.deleteLoyaltyProgram(this.deletingLoyalty.loyalty_program_id);
      this.deletingLoyalty = {};
      this.deleteDialog = false;
    },
    getFilteredData() {
      let sendingData = {
        query: `?page=${this.page}&q=${this.search ? this.search : ''}`,
      };
      this.fetchLoyaltyPrograms(sendingData.query);
    },
  },

  mounted() {
    this.fetchLoyaltyPrograms();
  },

  created() {
    this.debouncedSearch = debounce(this.handleSearch, 500);
  },

  beforeCreate() {
    if (!this.$route.query.page) {
      this.$router.push({
        query: {
          page: 1,
          ...this.$route.query,
        },
      });
    }
  },

  watch: {
    $route(val) {
      if (!val.query.page) {
        this.$router.push({
          query: {
            page: 1,
            ...this.$route.query,
          },
        });
      }
      this.getFilteredData();
    },

    page(page) {
      this.$router.push({ query: { ...this.$route.query, page } });
    },

    search(val) {
      if (val == null) {
        this.page = 1;
        this.getFilteredData();
      } else {
        this.debouncedSearch();
      }
    },
  },
};
</script>

<style></style>
